// src/components/Header/UpdateAddressModal.jsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Navigation, Check, ChevronDown } from "lucide-react";
import { getDatabase, ref, update, get ,set } from "firebase/database";
import app from "../../../firebase";

const db = getDatabase(app);

// Data for state and city dropdowns
const indianStatesAndCities = {
  "Andhra Pradesh": [
    "Alluri Sitharama Raju",
      "Anakapalli",
      "Annamayya",
      "Anantapuramu",
      "Bapatla",
      "Chittoor",
      "Dr. B.R. Ambedkar Konaseema",
      "East Godavari",
      "Eluru",
      "Guntur",
      "Kakinada",
      "Krishna",
      "Kurnool",
      "Palnadu",
      "NTR",
      "Parvathipuram Manyam",
      "Nandyal",
      "Prakasam",
      "Srikakulam",
      "Sri Potti Sri Ramulu Nellore",
      "Sri Sathya Sai",
      "Tirupati",
      "Visakhapatnam",
      "Vizianagaram",
      "West Godavari",
      "YSR Kadapa"
  ],
  "Telangana": [
     "Adilabad",
      "Bhadradri Kothagudem",
      "Hyderabad",
      "Jagtial",
      "Jangaon",
      "Jayashankar Bhupalpally",
      "Jogulamba Gadwal",
      "Kamareddy",
      "Karimnagar",
      "Khammam",
      "Kumuram Bheem Asifabad",
      "Mahabubabad",
      "Mahabubnagar",
      "Mancherial",
      "Medak",
      "Medchal Malkajgiri",
      "Mulugu",
      "Nagarkurnool",
      "Nalgonda",
      "Narayanpet",
      "Nirmal",
      "Nizamabad",
      "Peddapalli",
      "Rajanna Sircilla",
      "Ranga Reddy",
      "Sangareddy",
      "Siddipet",
      "Suryapet",
      "Vikarabad",
      "Wanaparthy",
      "Warangal",
      "Yadadri Bhuvanagiri"
  ]
};

const Toast = ({ message, type, onClose }) => {
    const bgColor = type === 'error' ? 'bg-red-500' : 'bg-green-500';
    
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed bottom-4 right-4 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg flex items-center justify-between min-w-[250px] z-50`}
        >
            <div className="flex items-center">
                {type === 'error' ? (
                    <X className="w-5 h-5 mr-2" />
                ) : (
                    <Check className="w-5 h-5 mr-2" />
                )}
                <span>{message}</span>
            </div>
            <button onClick={onClose} className="ml-4 text-white hover:text-gray-200">
                <X className="w-4 h-4" />
            </button>
        </motion.div>
    );
};

const UpdateAddressModal = ({ isOpen, onClose, initialLocation }) => {
    const [locationData, setLocationData] = useState({
        address: '',
        city: '',
        state: '',
        pincode: '',
    });
    const [cities, setCities] = useState([]);
    const [locationLoading, setLocationLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toasts, setToasts] = useState([]);

    useEffect(() => {
        if (isOpen && initialLocation) {
            setLocationData(initialLocation);
            if (initialLocation.state) {
                setCities(indianStatesAndCities[initialLocation.state] || []);
            }
        }
    }, [isOpen, initialLocation]);

    const addToast = (message, type = 'error') => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, message, type }]);
        setTimeout(() => removeToast(id), 5000);
    };

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const newData = { ...locationData, [name]: value };
        if (name === "state") {
            setCities(indianStatesAndCities[value] || []);
            newData.city = "";
        }
        setLocationData(newData);
    };

    const getCurrentLocation = () => {
        setLocationLoading(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    try {
                        const { latitude, longitude } = position.coords;
                        const response = await fetch(
                            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
                        );
                        const data = await response.json();
                        const address = data.address;
                        const detectedState = address.state || "";

                        if (detectedState && indianStatesAndCities[detectedState]) {
                            setCities(indianStatesAndCities[detectedState]);
                        } else {
                            setCities([]);
                        }

                        setLocationData({
                            address: data.display_name,
                            city: address.city || address.town || address.village || "",
                            state: detectedState,
                            pincode: address.postcode || "",
                        });
                        addToast("Location detected successfully!", "success");
                    } catch (error) {
                        addToast("Failed to fetch location details.");
                    } finally {
                        setLocationLoading(false);
                    }
                },
                (error) => {
                    setLocationLoading(false);
                    addToast("Location access denied. Please enable location services.");
                }
            );
        } else {
            setLocationLoading(false);
            addToast("Geolocation is not supported by your browser.");
        }
    };

    const handleUpdateAddress = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const customerId = localStorage.getItem('customerId');
        if (!customerId) {
            addToast("You must be logged in to update your address.", "error");
            setIsSubmitting(false);
            return;
        }

        try {
            // Reference the user's data in the database
            const userRef = ref(db, `Users/${customerId}`);
            
            // Check if user exists before updating
            const userSnapshot = await get(userRef);
            if (!userSnapshot.exists()) {
                 addToast("User not found.", "error");
                 setIsSubmitting(false);
                 return;
            }

            const oldData = userSnapshot.val();

            // Perform the update
            await update(userRef, {
                address: locationData.address,
                city: locationData.city,
                state: locationData.state,
                pincode: locationData.pincode,
            });

            // Update email reference if state or city changed
            if (oldData.state !== locationData.state || oldData.city !== locationData.city) {
                // Remove old email reference (if the user's location is changing)
                const oldEmailRef = ref(db, `CustomerEmails/${oldData.email.replace(/\./g, ',')}`);
                await update(oldEmailRef, {
                    state: locationData.state,
                    city: locationData.city,
                    phone: oldData.phone
                });

                // Update the user's data path
                const newUserId = `${locationData.state}/${locationData.city}/${oldData.phone}`;
                const newUserData = { ...oldData, ...locationData };
                
                await set(ref(db, `Users/${newUserId}`), newUserData);
                await set(ref(db, `Users/${customerId}`), null); // Delete old path

                localStorage.setItem('customerId', newUserId);
            }

            // Update local storage
            const updatedCustomerData = JSON.parse(localStorage.getItem('customerData'));
            localStorage.setItem('customerLocation', JSON.stringify({
                ...locationData,
                coordinates: updatedCustomerData.coordinates
            }));
            localStorage.setItem('customerData', JSON.stringify({
                ...updatedCustomerData,
                ...locationData,
                fullAddress: `${locationData.address}, ${locationData.city}, ${locationData.state} - ${locationData.pincode}`,
            }));

            // Dispatch a custom event to notify other components (like the Header) to update
            window.dispatchEvent(new Event('locationUpdated'));

            addToast("Address updated successfully!", "success");
            onClose();
        } catch (error) {
            console.error("Failed to update address:", error);
            addToast("Failed to update address. Please try again.", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <AnimatePresence>
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4"
                    >
                        <div className="flex justify-between items-center border-b pb-3 mb-4">
                            <h2 className="text-lg font-bold text-gray-800">Update Delivery Address</h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateAddress} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Address</label>
                                <textarea
                                    name="address"
                                    value={locationData.address}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    placeholder="Street, Area, Landmark"
                                    rows={2}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                                    <div className="relative">
                                        <select
                                            name="state"
                                            value={locationData.state}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white"
                                            required
                                        >
                                            <option value="">Select State</option>
                                            {Object.keys(indianStatesAndCities).map(state => (
                                                <option key={state} value={state}>{state}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                                    <div className="relative">
                                        <select
                                            name="city"
                                            value={locationData.city}
                                            onChange={handleInputChange}
                                            disabled={!locationData.state || cities.length === 0}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none bg-white disabled:bg-gray-100"
                                            required
                                        >
                                            <option value="">Select District</option>
                                            {cities.map(city => (
                                                <option key={city} value={city}>{city}</option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                                <input
                                    type="text"
                                    name="pincode"
                                    value={locationData.pincode}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                                    placeholder="123456"
                                    required
                                />
                            </div>

                            <button
                                type="button"
                                onClick={getCurrentLocation}
                                disabled={locationLoading}
                                className="w-full flex items-center justify-center text-sm text-orange-600 hover:text-orange-700 bg-orange-50 hover:bg-orange-100 py-2 px-4 rounded-lg transition-colors duration-200"
                            >
                                {locationLoading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Detecting...
                                    </>
                                ) : (
                                    <>
                                        <Navigation className="w-4 h-4 mr-2" />
                                        Use My Current Location
                                    </>
                                )}
                            </button>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-md flex items-center justify-center"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Updating...
                                    </>
                                ) : (
                                    "Update Address"
                                )}
                            </button>
                        </form>
                    </motion.div>
                </div>
            </AnimatePresence>

            <AnimatePresence>
                {toasts.map((toast) => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </AnimatePresence>
        </>
    );
};

export default UpdateAddressModal;